/**
 * Search / Go To — the pure half. Turns a free-text query into a ranked list of
 * places to go to, and pulls the named places out of the chart cells. Kept pure
 * and store-free so it can be unit-tested and so the sheet stays thin.
 *
 * Three kinds of hit are blended:
 *   - a typed position, in degrees-minutes-decimal (the way the charts and RYA
 *     courses work), parsed straight to a point;
 *   - charted places and objects (bays, sounds, harbours, buoys, lights…), by
 *     name, from the loaded cells;
 *   - the user's own marks and routes, by name.
 */
import type { Geometry, Position } from 'geojson';
import type { ChartFeature, ChartFeatureCollection } from '@/types';
import type { Route, Waypoint } from '@/routing';
import { parsePosition, formatPosition } from '@/nav';
import type { SearchPlace } from '@/store/searchIndex';

export type SearchResultKind = 'latlon' | 'place' | 'object' | 'mark' | 'route';

export interface SearchResult {
  id: string;
  /** Main line of the row. */
  label: string;
  /** Second line: what kind of thing it is / where it is. */
  sublabel: string;
  /** Where to centre and drop the go-to marker. [lon, lat]. */
  position: [number, number];
  kind: SearchResultKind;
}

/** S-57 groups we treat as a "charted object" rather than a "place". */
const OBJECT_GROUPS = new Set([
  'light',
  'buoy',
  'beacon',
  'wreck',
  'obstruction',
  'rock',
  'platform',
  'mooring',
]);

/** A representative point for any geometry: the point itself, or the mean of its
 * coordinates for a line/area, so a named bay or sound centres sensibly. */
export function representativePoint(geometry: Geometry | null | undefined): [number, number] | null {
  if (!geometry) return null;
  if (geometry.type === 'Point') {
    const c = geometry.coordinates;
    return [c[0], c[1]];
  }
  let sx = 0;
  let sy = 0;
  let n = 0;
  const walk = (coords: unknown): void => {
    if (Array.isArray(coords) && typeof coords[0] === 'number') {
      const p = coords as Position;
      sx += p[0];
      sy += p[1];
      n += 1;
      return;
    }
    if (Array.isArray(coords)) for (const c of coords) walk(c);
  };
  if ('coordinates' in geometry) walk(geometry.coordinates);
  else if (geometry.type === 'GeometryCollection') for (const g of geometry.geometries) {
    const p = representativePoint(g);
    if (p) {
      sx += p[0];
      sy += p[1];
      n += 1;
    }
  }
  return n > 0 ? [sx / n, sy / n] : null;
}

/** Pull every named feature out of the loaded cells, de-duplicated by name +
 * rough position so the same buoy from two overlapping cells appears once. */
export function extractNamedPlaces(cells: Iterable<ChartFeatureCollection>): SearchPlace[] {
  const out: SearchPlace[] = [];
  const seen = new Set<string>();
  for (const cell of cells) {
    for (const f of cell.features as ChartFeature[]) {
      const props = f.properties;
      const name = props?.attrs?.name;
      if (typeof name !== 'string' || !name.trim()) continue;
      const pos = representativePoint(f.geometry);
      if (!pos) continue;
      const key = `${name.toLowerCase()}@${pos[0].toFixed(3)},${pos[1].toFixed(3)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        id: props.id || key,
        name: name.trim(),
        kind: OBJECT_GROUPS.has(props.group) ? 'object' : 'place',
        objClass: props.objClass,
        position: pos,
      });
    }
  }
  return out;
}

/** A friendly description of a charted object class, e.g. "LIGHTS" -> "Light". */
function objClassLabel(objClass?: string): string {
  if (!objClass) return 'Charted object';
  const map: Record<string, string> = {
    LIGHTS: 'Light',
    BOYLAT: 'Lateral buoy',
    BOYCAR: 'Cardinal buoy',
    BOYSAW: 'Safe-water buoy',
    BOYISD: 'Isolated-danger buoy',
    BOYSPP: 'Special buoy',
    BCNLAT: 'Beacon',
    LNDMRK: 'Landmark',
    SEAARE: 'Sea area',
    LNDARE: 'Land',
    BUAARE: 'Built-up area',
    ACHARE: 'Anchorage',
    WRECKS: 'Wreck',
  };
  return map[objClass] ?? objClass;
}

/** Rank: a startsWith match beats a word-boundary match beats a loose contains. */
function score(name: string, q: string): number {
  const n = name.toLowerCase();
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  if (n.includes(` ${q}`)) return 2;
  if (n.includes(q)) return 3;
  return 99;
}

export interface SearchSources {
  places: SearchPlace[];
  marks: Waypoint[];
  routes: Route[];
}

const MAX_RESULTS = 40;

/** Build the ranked result list for a query against the live sources. */
export function buildSearchResults(query: string, sources: SearchSources): SearchResult[] {
  const q = query.trim().toLowerCase();
  const results: SearchResult[] = [];

  // 1) A typed position always wins the top slot.
  const pos = parsePosition(query);
  if (pos) {
    results.push({
      id: 'latlon',
      label: formatPosition(pos),
      sublabel: 'Go to this position',
      position: pos,
      kind: 'latlon',
    });
  }

  if (!q) return results;

  type Scored = { r: SearchResult; s: number };
  const scored: Scored[] = [];

  for (const m of sources.marks) {
    const name = m.name ?? '';
    const s = score(name, q);
    if (s < 99) scored.push({ r: { id: `mark:${m.id}`, label: name || 'Mark', sublabel: 'Your mark', position: m.position, kind: 'mark' }, s: s - 0.5 });
  }
  for (const rt of sources.routes) {
    const s = score(rt.name, q);
    const first = rt.waypoints[0]?.position;
    if (s < 99 && first) scored.push({ r: { id: `route:${rt.id}`, label: rt.name, sublabel: `Your route · ${rt.waypoints.length} waypoints`, position: first, kind: 'route' }, s: s - 0.5 });
  }
  for (const p of sources.places) {
    const s = score(p.name, q);
    if (s < 99) {
      scored.push({
        r: {
          id: `place:${p.id}`,
          label: p.name,
          sublabel: p.kind === 'object' ? objClassLabel(p.objClass) : 'Place',
          position: p.position,
          kind: p.kind,
        },
        s,
      });
    }
  }

  scored.sort((a, b) => (a.s - b.s) || a.r.label.localeCompare(b.r.label));
  for (const x of scored) {
    results.push(x.r);
    if (results.length >= MAX_RESULTS) break;
  }
  return results;
}
